import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import isObject from 'lodash/isObject';
import currencyFormatter from 'currency-formatter';
import styled from 'styled-components';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { APP_PROXY_URL, HUMAN_DATE_FORMAT } from '../utils/constants';
import { useNotifs } from '../utils/useNotif';
import { useLoadingToast } from '../utils/useLoadingToast';
import AddressModal from './addressModal/AddressModal';
import OrderNowModal from './OrderNowModal';
import { BundleTypes } from '../../components/BundleSettings/types';
import type { Discount } from './BundleUpdate';

import {
  addBabProductForSubscription,
  addNewPaymentMehod,
  changeSCL,
  getProductsImages,
  getProductVariantsImages,
  getSCAvailableSellingPlans,
  getShopSellingPlans,
  getShuffleNextProducts,
  getSubscriptionBillingAttemptRetries,
  getSubscriptionBillingAttempts,
  LineRequest,
  LineResponse,
  reactivateSc,
  requestPaymentChange,
  skipSc,
  skipShuffleProduct,
  sleep,
  UnauthorizedError,
  unskipShuffleProduct,
  updateBabProductForBundle,
} from './api';

import CardButton from '../components/Card/CardButton';
import PauseModal from './pauseModal/PauseModal';
import SubsDetailPayment from './SubsDetailPayment';
import SubsFrequency from './SubsFrequency';
import PhoneManagement from './PhoneManagement';
import SubsNextOrder from './SubsNextOrder';
import DiscountCodeCard from './DiscountCodeCard/DiscountCodeCard';
import SubsOrderFulfillment from './OrderFulfillment';
import {
  createNewAddonLine,
  getGidFromIdForResource,
  getIdFromGid,
  hasProductsOutOfStock,
  isGid,
  mergeGlobalProductInProduct,
  mergeNewBundlePropsInLine,
  mergeUpdatedDynamicBaBLines,
  updateSubscriptionWithUpdatedLine,
  updateVariantLine,
} from './utils';
import { useGlobalContext } from './state/GlobalContext';
import { stubProductImages } from './subsStubData';
import { useFetchCustomerAddress } from '../utils/useFetchCustomerAddress';
import { GlobalModalProvider } from '../state/GlobalModalContext';
import Addons from './Addons';
import { AddonsProvider } from './Addons/AddonsContext';
import Breadcrumbs from '../components/Breadcrumbs';
import Card from '../components/Card';
import { Moments } from './moments';
import LastOrder from './LastOrder';

import ActionsMenu, { IMenuOption } from '../components/actionsMenu';
import PaymentMethodPanel from './PaymentMethodPanel';
import PauseButton from './SubsDetailButtons/PauseButton';
import CancelButton from './SubsDetailButtons/CancelButton';
import OrderNowButton from './SubsDetailButtons/OrderNowButton';
import SkipNextButton from './SubsDetailButtons/SkipNextButton';
import RetryPaymentButton from './SubsDetailButtons/RetryPaymentButton';
import ReactivateButton from './SubsDetailButtons/ReactivateButton';
import CancelMitigationModal from './CancelMitigation/CancelMitigationModal';
import PauseMitigationModal from './PauseMitigation/PauseMitigationModal';
import LoadingErrorPage from '../components/LoadingErrorPage/LoadingErrorPage';
import { ModalManagerProvider } from '../components/ModalManager/ModalManagerContext';
import {
  generateNewDynamicSubscriptionLines,
  getBabUUID,
  getDynamicBabLinesByUUID,
  isDynamicLine,
  wrapDynamicBabItems,
} from '../utils/BundleUtils';

import ProductsTable from './ProductsTable';
import { BillingHistory } from './BillingHistory';
import { MerchantBanner } from '../components/MerchantBanner/MerchantBanner';
import { updateLocalization } from '../utils/localization';
import uniq from 'lodash/uniq';
import ReactivateModal from './ReactivateModal/ReactivateModal';
import PreviewLogo from '../../assets/svgs/PreviewLogo';
import {
  SubsDetailLayout,
  ActionColumLayout,
  HideSm,
  ShowSm,
  FlexContainer,
  ActionButtonContainer,
  SpinnerContainer,
  DetailWrapper,
  DetailTitle,
  ShipmentName,
  ShipmentBody,
  LinkButton,
  NotifAnchor,
  MenuWrapper,
  LocalDeliveryInfo,
} from './SubsDetail.components';
import { useSubDetailToast } from './state/useSubDetailToast';
import { useSubscription } from './state/useSubscription';
import { useSubDetail, useShopConfiguration } from './state/useSubDetail';
import { startMomentsApplicationsRefetchBackoff } from '../utils/momentsRefetchBackoff';
import { FEATURE_FLAGS, isFeatureFlagEnabled } from '../utils/featureFlags';

interface BaseMutationQuery {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
}

const CREDIT_CARD_EXISTS_ERROR = 'credit card already exists';

const COUNTRIES_TO_SHOW_PHONE_MANAGEMENT = ['United States', 'Canada'];

export default (): React.ReactElement => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { renderNotifs, addNotif, resetNotifs } = useNotifs();
  const { addLoadingToast, removeLoadingToast } = useLoadingToast();
  const { showErrorToast, showSuccessToast } = useSubDetailToast();
  useFetchCustomerAddress();

  const {
    customerId,
    subscriptionId,
    authToken,
    customerEmail,
    addonsDiscounts,
    addonsSellingPolicy,
    logout,
    shopNotifyBySMS,
    shopAddressCountry,
    customerPortalSettings,
    isShopifyLoggedIn,
    enableShuffleUpcomingProducts,
    setIsBundleUpdating,
    shopHasActiveMoments,
    setBundleIsReady,
  } = useGlobalContext();
  const [subscriptionBillingAttempts, setSubscriptionBillingAttempts] =
    useState<SubscriptionBillingAttempts>();
  const [subscriptionBillingAttemptRetry, setSubscriptionBillingAttemptRetry] =
    useState<SubscriptionBillingAttemptRetry>();

  const [showOrderNowConfirmationModal, setShowOrderNowConfirmationModal] =
    useState(false);

  const openOrderNowConfirmationModal = useCallback(() => {
    setShowOrderNowConfirmationModal(true);
  }, []);

  const closeOrderNowConfirmationModal = useCallback(() => {
    setShowOrderNowConfirmationModal(false);
  }, []);

  const _subDetail = useSubDetail();
  const {
    forceUpdateError,
    isLoadingNewPaymentMethod,
    isLoadingOrderNow,
    isLoadingReactivate,
    isLoadingShippingRate,
    isMerchantBannerOpen,
    isNewPaymentSaved,
    isUpdatingShuffleProduct,
    modalOpen,
    nextBAR,
    originOrder,
    paymentResponse,
    productImages,
    sellingPlans,
    sendEmailIfAddPaymentFails,
    setDisableOpenButton,
    setIsLoadingNewPaymentMethod,
    setIsLoadingReactivate,
    setIsLoadingShippingRate,
    setIsMerchantBannerOpen,
    setIsNewPaymentSaved,
    setIsUpdatingShuffleProduct,
    setModalOpen,
    setPaymentResponse,
    setProductImages,
    setSellingPlans,
    setSendEmailIfAddPaymentFails,
    setShowPauseModal,
    setShowReactivateModal,
    setSubscriptionIsInDunning,
    showPauseModal,
    showReactivateModal,
    subscriptionIsInDunning,
    subscriptionQuantityChanges,
  } = _subDetail;

  const {
    isActive,
    isCancelled,
    isExpired,
    isFailed,
    isPaused,
    isRetryingManually,
    isUpdatingPaymentMethod,
    subscription,
    onOrderNowConfirm,
    orderSubmitted,
    shouldDisableOrderNowButton,
    setShouldDisableOrderNowButton,
  } = useSubscription({
    ..._subDetail,
    closeOrderNowConfirmationModal,
    queryClient,
    customerId,
    subscriptionId,
    authToken,
  });

  useEffect(() => {
    //TODO this could be added in the subscription global object
    setShouldDisableOrderNowButton(
      !!subscription?.OriginalNextBillingDate ||
        hasProductsOutOfStock(subscription?.Lines),
    );
  }, [
    setShouldDisableOrderNowButton,
    subscription?.Lines,
    subscription?.OriginalNextBillingDate,
  ]);

  const shopConfiguration = useShopConfiguration(subscription);

  const [deliveryInterval, deliveryIntervalCount] =
    subscription?.DeliveryIntervalDeliveryCount.split('#') || ['MONTH', 1];

  const shouldGetBaBImages = () => {
    const isBaB = subscription?.Lines.some(({ CustomAttributes = [] }) => {
      return (
        CustomAttributes.some(({ key }) => key === '_bundleBoxSize') ||
        CustomAttributes.some(
          ({ key, value }) =>
            key === '_bundleType' && value === BundleTypes.classic,
        )
      );
    });

    return !!isBaB;
  };

  const getBaBLinesProductVariantsIds = () => {
    let ids: string[] = [];
    subscription?.Lines?.forEach((line) => {
      line.CustomAttributes?.forEach((attribute) => {
        if (attribute.key.startsWith('_pv_')) {
          const productId = getGidFromIdForResource(
            attribute.key.replace('_pv_', ''),
            'ProductVariant',
          );
          ids = [...ids, productId];
        }
      });
    });
    return ids;
  };

  const { data: babProductVariants } = useQuery(
    ['images', customerId, subscriptionId, getBaBLinesProductVariantsIds()],
    async () => {
      const data = await getProductVariantsImages(
        customerId,
        subscriptionId,
        authToken,
        getBaBLinesProductVariantsIds(),
      );

      return data;
    },
    {
      enabled: shouldGetBaBImages(),
    },
  );

  const isSubscriptionInHeldOfInventory = useMemo(
    () =>
      subscription?.Lines
        ? subscription.Lines.some((line) => {
            return (
              line.IsSoldOut ||
              (line?.BaBOutOfStockVariants &&
                line?.BaBOutOfStockVariants?.length > 0)
            );
          })
        : false,
    [subscription],
  );

  const removeLine = (old, data) => ({
    ...old,
    Lines: subscription?.Lines?.filter(
      ({ SubscriptionContractLineId }) =>
        SubscriptionContractLineId !== data.lines[0].id,
    ),
  });

  const subscriptionLineMutation = useMutation(
    ({ customerId, subscriptionId, authToken, lines }) =>
      changeSCL(lines, customerId, subscriptionId, authToken),

    {
      onMutate: async (
        data: BaseMutationQuery & {
          lines: LineRequest[];
          mutationType: 'REMOVE_LINE' | 'UPDATE_LINE';
        },
      ) => {
        addLoadingToast();
        setIsLoadingShippingRate(true);
        const previousValue = queryClient.getQueryData<Subscription>([
          'subscription',
          customerId,
          subscriptionId,
        ]);

        if (data.mutationType === 'REMOVE_LINE') {
          queryClient.setQueryData(
            ['subscription', customerId, subscriptionId],
            (old) => {
              return removeLine(old, data);
            },
          );
        }

        return previousValue;
      },
      // On failure, roll back to the previous value
      onError: (err, variables, previousValue) => {
        if (err instanceof UnauthorizedError) {
          logout();
        }
        removeLoadingToast();
        setIsLoadingShippingRate(false);
        if (variables?.mutationType === 'REMOVE_LINE') {
          showErrorToast(t('subscription.remove_line_error'));

          queryClient.setQueryData(
            ['subscription', customerId, subscriptionId],
            previousValue,
          );
        } else {
          showErrorToast(t('subscription.update_error'));
          //TODO handle updating the UI
          queryClient.invalidateQueries([
            'subscription',
            customerId,
            subscriptionId,
          ]);
        }
      },
      onSuccess: (data, variables) => {
        removeLoadingToast();
        setIsLoadingShippingRate(false);
        if (variables.mutationType === 'REMOVE_LINE') {
          showSuccessToast(t('subscription.remove_line_success'));
        } else {
          showSuccessToast(t('subscription.update_success'));
        }
        if (shopHasActiveMoments) {
          startMomentsApplicationsRefetchBackoff({
            queryClient,
            subscriptionId,
          });
        }
        queryClient.setQueryData<Subscription>(
          ['subscription', customerId, subscriptionId],
          (old) =>
            updateSubscriptionWithUpdatedLine({
              ...old,
              ...data.subscriptionContract,
            }),
        );
      },
    },
  );

  // If isPreview, show small "Awtomic Preview" modal, which closes the window on click
  const isPreview = new URLSearchParams(window?.location?.search).get(
    'preview',
  );
  const isStubbedData = subscriptionId?.includes('TEST');

  const isPrepaid = !!subscription?.HasPrepaidLines;
  const hasPaymentMethod =
    !!subscription?.CustomerPaymentMethod?.instrument?.__typename;

  const [
    isCurrencyDifferentThanShopDefaultCurrency,
    setIsCurrencyDifferentThanShopDefaultCurrency,
  ] = useState(false);

  // Check if subscription currency or country is different than current market and update market accordingly
  useEffect(() => {
    const scCurrency = subscription?.CurrencyCode;
    const scCountry =
      subscription?.MarketOverrideCountryCode ||
      subscription?.DeliveryMethod?.address?.countryCode;

    const shopifyCurrency = window?.Shopify?.currency?.active;
    const shopifyCountry = window?.Shopify?.country;

    const searchParams = new URLSearchParams(window?.location?.search);
    const withLocalization = searchParams.get('locred'); // check if redirect has happened already

    // Sometimes the window object has a market but in the BE it has been changed to the default one
    // so we are calling this endpoint to enforce the market we want in the FE in the BE requests too
    if (scCurrency && scCountry) {
      try {
        fetch(
          `/services/currency/update?currency=${scCurrency}&country=${scCountry}`,
          {
            method: 'GET',
            redirect: 'manual', // avoid redirecting so we don't go back to the previous market
          },
        );
      } catch (error) {
        console.error(error);
      }
    }

    if (
      (shopifyCurrency && scCurrency && shopifyCurrency != scCurrency) ||
      (shopifyCountry && scCountry && shopifyCountry != scCountry)
    ) {
      if (!withLocalization) {
        const returnTo = new URL(window?.location?.href);
        returnTo.searchParams.set('locred', '1');
        returnTo.searchParams.delete('country');
        returnTo.searchParams.delete('currency');

        updateLocalization({
          currencyCode: scCurrency,
          countryCode: scCountry,
          returnTo: returnTo.href,
        });
      } else {
        // clear params from url
        const currentUrl = new URL(window?.location?.href);
        currentUrl.searchParams.delete('locred');
        currentUrl.searchParams.delete('country');
        currentUrl.searchParams.delete('currency');
        window.history.replaceState({}, '', currentUrl.href);

        // if after setting localization currency is still different, we block edits
        if (shopifyCurrency && scCurrency && shopifyCurrency != scCurrency) {
          setIsCurrencyDifferentThanShopDefaultCurrency(true);
        }
      }
    }
  }, [subscription?.CurrencyCode, subscription?.DeliveryMethod]);

  useEffect(() => {
    if (subscription && !hasPaymentMethod) {
      addNotif(
        subscription.ContainsGift ? (
          t('subscription.no_payment_method_error_gift')
        ) : (
          <>
            {t('subscription.no_payment_method_error')}{' '}
            <NotifAnchor onClick={handleOnAddNewPaymentMethodClick}>
              {t('subscription.no_payment_method_error_add')}
            </NotifAnchor>
          </>
        ),
        {
          type: 'error',
        },
      );
    }
  }, [subscription, hasPaymentMethod]);

  useEffect(() => {
    const fetchProductsImages = async (
      subscriptionId: string,
      ids: string[],
      fallbackImages,
    ) => {
      if (!subscriptionId?.includes('TEST')) {
        setProductImages(fallbackImages);
        try {
          const products = await getProductsImages({
            customerId,
            authToken,
            products: ids,
            subscriptionId,
          });

          const productImages =
            products?.map?.((product) => ({
              id: getIdFromGid(product.gId),
              featuredImage: product.featuredImage?.url,
              variants: product.variants.map((variant) => ({
                id: getIdFromGid(variant.gId),
                featuredImage: variant.image?.url,
              })),
            })) || [];

          setProductImages(productImages);
        } catch (e) {
          console.error(e);
        }
      } else {
        setProductImages(stubProductImages(subscription));
      }
    };

    if (subscription?.Lines?.length) {
      const ids = subscription.Lines.map((line) =>
        // After swapping, the product id in the line is just the id
        isGid(line.ProductId)
          ? line.ProductId
          : getGidFromIdForResource(line.ProductId, 'Product'),
      );

      const subscriptionId = getIdFromGid(subscription.SubscriptionId) || '';

      const fallbackImages = subscription.Lines.map((line) => ({
        id: getIdFromGid(line.ProductId),
        featuredImage: line.VariantImageSrc,
        variants: [
          {
            id: getIdFromGid(line.VariantId),
            featuredImage: line.VariantImageSrc,
          },
        ],
      }));

      fetchProductsImages(subscriptionId, ids, fallbackImages);
    }
  }, [subscription, authToken, customerId]);

  useEffect(() => {
    const fetchSellingPlans = async () => {
      if (!subscriptionId?.includes('TEST')) {
        try {
          const { sellingPlans = [] } =
            (await getShopSellingPlans({
              customerId,
              authToken,
              shopDomain: subscription.ShopDomain,
            })) || {};

          setSellingPlans(sellingPlans);
        } catch (e) {
          console.error(e);
        }
      }
    };

    if (subscription) {
      fetchSellingPlans();
    }
  }, [subscription, authToken, customerId]);

  const [availableSellingPlans, setAvailableSellingPlans] = useState<any>();

  useEffect(() => {
    const fetchAvailableSellingPlans = async () => {
      try {
        const sellingPlanIds = subscription.Lines.map(
          (line) => line.SellingPlanId,
        );
        const sellingPlans = await getSCAvailableSellingPlans(
          sellingPlanIds,
          customerId,
          getIdFromGid(subscription.SubscriptionId),
          authToken,
        );

        setAvailableSellingPlans(sellingPlans);
      } catch (e) {
        console.error(e);
      }
    };

    if (subscription && authToken && customerId && !availableSellingPlans) {
      fetchAvailableSellingPlans();
    }
  }, [subscription, authToken, availableSellingPlans]);

  const [shuffleLineData, setShuffleLineData] = useState<
    Record<
      string,
      {
        skipAllowed: boolean;
        upcomingProducts: ShuffleNextProduct[];
        previousProducts: ShuffleNextProduct[];
        remainingProducts: ShuffleNextProduct[];
      }
    >
  >({});
  const [shouldFetchVariantImages, setShouldFetchVariantImages] =
    useState(false);

  useEffect(() => {
    const fetchUpcomingShuffle = async () => {
      const shuffleLines = subscription?.Lines?.filter(
        ({ CustomAttributes }) =>
          CustomAttributes?.some(
            ({ key, value }) => key === '_bundleType' && value === 'shuffle',
          ),
      );

      const responses = {};

      await Promise.all(
        shuffleLines.map(async ({ SubscriptionContractLineId }) => {
          responses[SubscriptionContractLineId] = await getShuffleNextProducts(
            getIdFromGid(subscription.SubscriptionId),
            getIdFromGid(SubscriptionContractLineId),
            authToken,
          );
        }),
      );

      setShuffleLineData(responses);
      setShouldFetchVariantImages(true);
    };

    if (subscription && authToken && enableShuffleUpcomingProducts) {
      fetchUpcomingShuffle().then();
    }
  }, [subscription, authToken]);

  useEffect(() => {
    const fetchVariantImages = async () => {
      const variantIds: string[] = uniq(
        Object.values(shuffleLineData).flatMap(
          ({ upcomingProducts, remainingProducts, previousProducts }) => [
            ...upcomingProducts.map((product) => product.variantId),
            ...remainingProducts.map((product) => product.variantId),
            ...previousProducts.map((product) => product.variantId),
          ],
        ),
      );

      const imageResponse = await getProductVariantsImages(
        customerId,
        getIdFromGid(subscription.SubscriptionId),
        authToken,
        variantIds,
      );

      setShouldFetchVariantImages(false);

      // only update if there were any variant images returned
      if (imageResponse.length) {
        // update upcoming products with variant images
        const updatedShuffleLineData = Object.entries(shuffleLineData).reduce(
          (
            acc,
            [
              lineId,
              {
                skipAllowed,
                upcomingProducts,
                remainingProducts,
                previousProducts,
              },
            ],
          ) => {
            acc[lineId] = {
              skipAllowed,
              upcomingProducts: upcomingProducts.map((product) => {
                const response = imageResponse.find(
                  ({ gId }) => gId === product.variantId,
                );
                const variantImage = response?.image?.url;
                const featuredImage = response?.product?.featuredImage?.url;

                return {
                  ...product,
                  variantImage: variantImage || featuredImage,
                };
              }),
              remainingProducts: remainingProducts.map((product) => {
                const response = imageResponse.find(
                  ({ gId }) => gId === product.variantId,
                );
                const variantImage = response?.image?.url;
                const featuredImage = response?.product?.featuredImage?.url;

                return {
                  ...product,
                  variantImage: variantImage || featuredImage,
                };
              }),
              previousProducts: previousProducts.map((product) => {
                const response = imageResponse.find(
                  ({ gId }) => gId === product.variantId,
                );
                const variantImage = response?.image?.url;
                const featuredImage = response?.product?.featuredImage?.url;

                return {
                  ...product,
                  variantImage: variantImage || featuredImage,
                };
              }),
            };

            return acc;
          },
          {} as Record<
            string,
            {
              skipAllowed: boolean;
              upcomingProducts: ShuffleNextProduct[];
              remainingProducts: ShuffleNextProduct[];
              previousProducts: ShuffleNextProduct[];
            }
          >,
        );

        setShuffleLineData(updatedShuffleLineData);
      }
    };

    if (shouldFetchVariantImages) {
      fetchVariantImages().then();
    }
  }, [shuffleLineData, shouldFetchVariantImages]);

  const handleClickClosePreview = () => {
    window?.close();
  };

  const handleOnAddNewPaymentMethodClick = () => {
    resetNotifs();
    onAddNewPaymentMethod();
  };

  const {
    isOpen: isPaymentMethodPanelOpen,
    onOpen: onAddNewPaymentMethod,
    onClose: onClosePaymentMethodPanel,
  } = useDisclosure();

  const {
    isOpen: isCancelModalOpen,
    onOpen: onCancelModalOpen,
    onClose: onCancelModalClose,
  } = useDisclosure();

  const {
    isOpen: isPauseModalOpen,
    onOpen: onPauseModalOpen,
    onClose: onPauseModalClose,
  } = useDisclosure();

  const handleOnCancelModalClose = (message?: string | ReactElement) => {
    if (message) {
      showSuccessToast(message);
    }

    onCancelModalClose();
  };

  const handleOnPauseModalClose = (message?: string) => {
    if (message) {
      showSuccessToast(message);
    }

    onPauseModalClose();
  };

  const fetchSubBillingAttempts = useCallback(async () => {
    try {
      let response;
      if (!subscriptionId?.includes('TEST')) {
        response = await getSubscriptionBillingAttempts(
          customerId,
          subscriptionId,
          authToken,
        );

        // make this data easily available on the window for custom theme dev purposes
        window.Awtomic = window.Awtomic || {};
        window.Awtomic.subscriptionBillingAttempts = response.Items || [];
      }

      setSubscriptionBillingAttempts(response);
    } catch (err: any) {
      console.error(err);

      if (err instanceof UnauthorizedError) {
        logout();
      }
    }
  }, [customerId, subscriptionId, authToken]);

  const fetchSubBillingAttemptRetries = useCallback(async () => {
    try {
      const BARs = await getSubscriptionBillingAttemptRetries(
        customerId,
        subscriptionId,
        authToken,
      );

      // make this data easily available on the window for custom theme dev purposes
      window.Awtomic = window.Awtomic || {};
      window.Awtomic.subscriptionBillingAttemptRetries = BARs;

      if (BARs?.length) {
        const futureBAR = BARs.find((bar) => {
          const nextBillingDate = dayjs(bar.NextBillingDate);
          const now = dayjs();

          return nextBillingDate.diff(now) > 0;
        });
        nextBAR.current = futureBAR;
        setSubscriptionBillingAttemptRetry(futureBAR);
      }
    } catch (err: any) {
      // We don't want to set the error here, as it means the page goes empty
      // and show a general error. We have a default dunning process message
      // if no billing attempt retry is in here. So
      console.error(err);

      if (err instanceof UnauthorizedError) {
        logout();
      }
    }
  }, [customerId, subscriptionId, authToken]);

  const saveHandler = async (newSubscription, line) => {
    const { Lines } = newSubscription;
    const lastSaveLines = subscriptionQuantityChanges.current?.Lines;

    const newLinesPayload = Lines.filter(
      ({ SubscriptionContractLineId }) =>
        line.SubscriptionContractLineId === SubscriptionContractLineId,
    ).map(({ SubscriptionContractLineId, Quantity }) => {
      const formerLine = lastSaveLines?.find(
        (line) =>
          line.SubscriptionContractLineId === SubscriptionContractLineId,
      );
      return {
        id: SubscriptionContractLineId,
        input: { quantity: Quantity, previousQuantity: formerLine?.Quantity },
      };
    });

    subscriptionQuantityChanges.current = newSubscription;

    const {
      input: { quantity },
    } = newLinesPayload[0];

    subscriptionLineMutation.mutate({
      lines: newLinesPayload,
      customerId,
      subscriptionId,
      authToken,
      mutationType: quantity === 0 ? 'REMOVE_LINE' : 'UPDATE_LINE',
    });
  };

  const debouncedSave = useCallback(
    debounce(
      (newSubscription, line) => saveHandler(newSubscription, line),
      1000,
    ),
    [],
  );

  useEffect(() => {
    fetchSubBillingAttempts();
  }, [fetchSubBillingAttempts]);
  const getShouldExpireContract = (subscription) => {
    const isAutorenew = !subscription?.BillingPolicyMaxCycles;
    let linesAtBeginningOfCycle = 0;
    subscription?.Lines.forEach(
      ({ PrepaidV2Orders, PrepaidOrders, SuccessfulBillingAttempts = [] }) => {
        const prepaidOrders = PrepaidV2Orders || PrepaidOrders;
        if (SuccessfulBillingAttempts?.length % prepaidOrders === 0) {
          linesAtBeginningOfCycle++;
        }
      },
    );

    return (
      subscription?.Lines?.length === linesAtBeginningOfCycle && isAutorenew
    );
  };

  const shouldExpireContract = useMemo(
    () => getShouldExpireContract(subscription),
    [subscription],
  );
  useEffect(() => {
    let dunning = false;
    if (subscription?.IsInDunning) {
      dunning = subscription?.IsInDunning;
    }

    setSubscriptionIsInDunning(dunning);
    dunning && setShouldDisableOrderNowButton(true);
  }, [subscription, subscriptionBillingAttempts]);

  // Remove this once the issue with Dwell is fixed
  const urlParams = new URLSearchParams(window.location.search);
  const shouldSkipBARs = urlParams.get('skipBARs') === 'true';

  useEffect(() => {
    if (subscriptionIsInDunning && !shouldSkipBARs) {
      fetchSubBillingAttemptRetries();
    }
  }, [subscriptionIsInDunning, fetchSubBillingAttemptRetries, shouldSkipBARs]);

  const handleAddressUpdate = (res: SaveShippingMethodResponse) => {
    addNotif(t('subscription.address_update_success'));

    if (subscription) {
      queryClient.setQueryData<Subscription>(
        ['subscription', customerId, subscriptionId],
        (old) => ({
          ...(old as Subscription),
          FormattedDeliveryPrice: res.FormattedDeliveryPrice,
          DeliveryMethod: {
            ...subscription.DeliveryMethod,
            ...res.DeliveryMethod,
          },
        }),
      );
    }
  };

  const handleFrequencyUpdate = (res: SubscriptionFrequency, msg: string) => {
    showSuccessToast(msg);
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...res,
      }),
    );
  };

  const handleCustomerUpdate = (
    msg: string,
    newValues: {
      Phone: string;
      AcceptsSMSMarketing: boolean;
    },
  ) => {
    showSuccessToast(msg);

    if (subscription) {
      queryClient.setQueryData<Subscription>(
        ['subscription', customerId, subscriptionId],
        (old) => ({
          ...(old as Subscription),
          Customer: {
            ...subscription.Customer,
            ...newValues,
          },
        }),
      );
    }
  };

  const handlePauseUpdate = (
    res: Subscription,
    msg: string,
    updatedDate = false,
  ) => {
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...res,
        ...(updatedDate && { OriginalNextBillingDate: undefined }),
      }),
    );
    if (shopHasActiveMoments) {
      startMomentsApplicationsRefetchBackoff({
        queryClient,
        subscriptionId,
      });
    }
    showSuccessToast(msg);
  };

  const handleSkipUpdate = (msg: string) => {
    queryClient.invalidateQueries(['subscription', customerId, subscriptionId]);
    showSuccessToast(msg);
  };

  const getSubscriptionLine = (
    lines: Subscription['Lines'],
    lineId: string,
  ) => {
    return lines?.find((line) => line.SubscriptionContractLineId === lineId);
  };

  const getLinesToDelete = (lines: Subscription['Lines'], lineId: string) => {
    const line = getSubscriptionLine(lines, lineId);
    const isDynamicBaBLine = line ? !!isDynamicLine(line) : false;

    if (!isDynamicBaBLine) {
      return [{ id: lineId, input: { quantity: 0 } }];
    }

    const BaBUUId = line ? getBabUUID(line) : '';
    const dynamicBabLines = getDynamicBabLinesByUUID(
      subscription.Lines,
      BaBUUId,
    );

    return dynamicBabLines.map((line) => ({
      id: line.SubscriptionContractLineId,
      input: { quantity: 0 },
    }));
  };

  const handleRemoveClick = async (lineId: string) => {
    const lines = getLinesToDelete(subscription.Lines, lineId);
    subscriptionLineMutation.mutate({
      lines,
      customerId,
      subscriptionId,
      authToken,
      mutationType: 'REMOVE_LINE',
    });
  };

  const handleSkipClick = async (lineId: string, productGId: string) => {
    setIsUpdatingShuffleProduct(true);
    addLoadingToast();
    const response = await skipShuffleProduct(
      getIdFromGid(subscription.SubscriptionId),
      getIdFromGid(lineId),
      productGId,
      authToken,
    );
    removeLoadingToast();

    setShuffleLineData({
      ...shuffleLineData,
      [lineId]: response,
    });

    showSuccessToast(t('subscription.skip_shuffle_product'));
    setIsUpdatingShuffleProduct(false);
    setShouldFetchVariantImages(true);
  };

  const handleUnskipClick = async (lineId: string, productGId: string) => {
    setIsUpdatingShuffleProduct(true);
    addLoadingToast();
    const response = await unskipShuffleProduct(
      getIdFromGid(subscription.SubscriptionId),
      getIdFromGid(lineId),
      productGId,
      authToken,
    );
    removeLoadingToast();

    setShuffleLineData({
      ...shuffleLineData,
      [lineId]: response,
    });

    showSuccessToast(t('subscription.skip_shuffle_product'));
    setIsUpdatingShuffleProduct(false);
    setShouldFetchVariantImages(true);
  };

  const handlePaymentUpdate = async () => {
    try {
      const res = await requestPaymentChange(
        customerId,
        subscriptionId,
        authToken,
      );
      const _customerEmail = customerEmail || subscription?.Customer?.Email;

      setPaymentResponse(res);

      if (sendEmailIfAddPaymentFails) {
        showErrorToast(
          t('subscription.update_payment_success_payment_error', {
            email: _customerEmail,
          }),
        );
      } else {
        showSuccessToast(
          t(
            subscriptionIsInDunning
              ? 'subscription.update_payment_success_dunning'
              : 'subscription.update_payment_success',
            { email: _customerEmail },
          ),
          60000, // 60 seconds
        );
      }
    } catch (err) {
      console.error(err);
      addNotif(t('subscription.update_payment_error'), { type: 'error' });
    }
  };

  const refetchUpcomingShuffleProducts = async () => {
    const shuffleLineDataWithoutImages = Object.keys(shuffleLineData).reduce(
      (acc, key) => {
        return {
          ...acc,
          [key]: {
            skipAllowed: shuffleLineData[key].skipAllowed,
            upcomingProducts: shuffleLineData[key].upcomingProducts.map(
              ({ variantImage, ...rest }) => ({ ...rest }),
            ),
            remainingProducts: shuffleLineData[key].remainingProducts.map(
              ({ variantImage, ...rest }) => ({ ...rest }),
            ),
            previousProducts: shuffleLineData[key].previousProducts.map(
              ({ variantImage, ...rest }) => ({ ...rest }),
            ),
          },
        };
      },
      {},
    );
    let newShuffleLineData = { ...shuffleLineDataWithoutImages };
    const currentShuffleLineData = { ...shuffleLineDataWithoutImages };

    addLoadingToast();

    while (isEqual(currentShuffleLineData, newShuffleLineData)) {
      const shuffleLines = subscription?.Lines?.filter(
        ({ CustomAttributes }) =>
          CustomAttributes?.some(
            ({ key, value }) => key === '_bundleType' && value === 'shuffle',
          ),
      );

      const responses = {};

      await Promise.all(
        shuffleLines.map(async ({ SubscriptionContractLineId }) => {
          responses[SubscriptionContractLineId] = await getShuffleNextProducts(
            getIdFromGid(subscription.SubscriptionId),
            getIdFromGid(SubscriptionContractLineId),
            authToken,
          );
        }),
      );
      newShuffleLineData = { ...responses };

      await sleep(500);
    }
    setShuffleLineData(newShuffleLineData);
    removeLoadingToast();
  };

  const refetchUntilChange = async ({ compareOnlyLines = false }) => {
    //refetch the subscriptions until it changes
    const currentSubscriptionContract = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);
    let newSubscriptionData = currentSubscriptionContract;

    while (
      isEqual(
        compareOnlyLines ? newSubscriptionData.Lines : newSubscriptionData,
        compareOnlyLines
          ? currentSubscriptionContract.Lines
          : currentSubscriptionContract,
      )
    ) {
      newSubscriptionData = await queryClient.fetchQuery([
        'subscription',
        customerId,
        subscriptionId,
      ]);
      sleep(500);
    }
    setBundleIsReady(true);
    launchBabUpdatedCompleteEvent(
      newSubscriptionData.Lines,
      newSubscriptionData,
      newSubscriptionData?.DiscountCodes,
    );
  };

  const handleOrderNowClick = async (manualRetry: boolean = false) => {
    await onOrderNowConfirm(manualRetry);
    const shuffleLines = subscription?.Lines?.filter(
      ({ CustomAttributes }) =>
        CustomAttributes?.some(
          ({ key, value }) => key === '_bundleType' && value === 'shuffle',
        ),
    );
    if (shuffleLines.length > 0) {
      await refetchUpcomingShuffleProducts();
    }
  };

  const handleNewPaymentSave = async (
    values: FormNewPaymentMethod | undefined,
  ) => {
    let needToTimeout = true;
    try {
      setIsLoadingNewPaymentMethod(true);
      const res = await addNewPaymentMehod({
        customerId,
        subscriptionId: getIdFromGid(subscription?.SubscriptionId),
        authToken,
        data: values,
        subscriptionIsInDunning,
      });
      const { customerPaymentMethod } = res;
      const nextBARDate = nextBAR.current?.NextBillingDate;
      const newSubscription: Subscription = {
        ...(subscription as Subscription),
        CustomerPaymentMethod: customerPaymentMethod,
      };
      const isInBillingProcess =
        subscription?.IsBilling || subscription?.IsBillingScheduled;

      if (newSubscription.UpdatingPaymentMethod && !isInBillingProcess) {
        handleNewPaymentMethodSuccess(
          newSubscription,
          <>
            {t('subscription.new_payment_method_dunning.message', {
              date: nextBARDate
                ? new Date(nextBARDate).toLocaleDateString()
                : '',
            })}{' '}
            <LinkButton
              className="subscription-payment-link-button"
              onClick={() => handleOrderNowClick(true)}
            >
              {t('subscription.new_payment_method_dunning.cta')}
            </LinkButton>
          </>,
        );
      } else {
        handleNewPaymentMethodSuccess(
          newSubscription,
          t('subscription.new_payment_method_success'),
        );
      }
      setIsNewPaymentSaved(true);
    } catch (err: any) {
      needToTimeout = false;
      console.error(err, err?.message);

      let parsedError;

      try {
        parsedError = JSON.parse(err?.message);
      } catch (parsingError) {
        parsedError = err?.message;
      }

      if (Array.isArray(parsedError)) {
        const [firstError] = parsedError;

        if (
          parsedError.length === 1 &&
          firstError.message === CREDIT_CARD_EXISTS_ERROR
        ) {
          handlePaymentUpdate();
        } else {
          showErrorToast(
            firstError.message,
            t('subscription.new_known_payment_method_error'),
          );
        }
      } else {
        showErrorToast(t('subscription.new_payment_method_error'));
      }
    } finally {
      // We give time so the webhook is fired
      setTimeout(
        () => {
          setIsLoadingNewPaymentMethod(false);
          needToTimeout && onClosePaymentMethodPanel();
        },
        needToTimeout ? 3000 : 0,
      );
    }
  };

  const handleNewPaymentMethodSuccess = (
    newSubscription: Subscription,
    msg:
      | string
      | React.ReactElement<any, string | React.JSXElementConstructor<any>>,
  ) => {
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );
    showSuccessToast(msg, 60000); // 1 minute
  };

  // control any update on subscription over stub data
  const handleStubDataChanges = () => {
    window.alert('this a test, no info will be saved');
  };

  if (!subscription) {
    return (
      <SpinnerContainer className="bundle-loading-container">
        <Spinner size="xl" style={{ margin: '0 auto', display: 'block' }} />
      </SpinnerContainer>
    );
  }

  const handleSkip = async (intervalId?: string) => {
    try {
      addLoadingToast();
      const { nextBillingDate } = await skipSc(
        customerId,
        getIdFromGid(subscription.SubscriptionId),
        authToken,
        intervalId,
      );
      const msg = t('subscription.skip_billing_interval_success', {
        date: dayjs(nextBillingDate).format(HUMAN_DATE_FORMAT),
      });

      removeLoadingToast();
      handleSkipUpdate(msg);
      if (shopHasActiveMoments) {
        startMomentsApplicationsRefetchBackoff({
          queryClient,
          subscriptionId,
        });
      }
    } catch (err) {
      removeLoadingToast();
      console.error(err);
      isStubbedData
        ? handleStubDataChanges()
        : showErrorToast(t('subscription.skip_billing_interval_error'));
    }
  };

  const handleOnQuantityChange = (
    quantity: number,
    line,
    action: 'INCREASE' | 'DECREASE',
  ): void => {
    let newSubscription: Subscription = {
      ...(subscription as Subscription),
      Lines: subscription.Lines?.map((currentLine) => {
        const {
          CurrentPriceAmount,
          PricingPolicyBasePriceAmount,
          CurrentPriceCurrencyCode,
        } = currentLine;
        if (
          line.SubscriptionContractLineId ===
          currentLine.SubscriptionContractLineId
        ) {
          let currentQuantity = quantity;
          if (action === 'INCREASE') {
            currentQuantity++;
          } else if (action === 'DECREASE' && currentQuantity > 0) {
            currentQuantity--;
          }

          const lineTotal =
            parseFloat(PricingPolicyBasePriceAmount || '0') * currentQuantity;

          const discountedLineTotal =
            parseFloat(CurrentPriceAmount) * currentQuantity;

          return {
            ...currentLine,
            Quantity: currentQuantity,
            FormattedLineTotal: currencyFormatter.format(discountedLineTotal, {
              code: CurrentPriceCurrencyCode,
            }),
            LineDiscountedPriceAmount: discountedLineTotal.toString(),
            FormattedPricingPolicyBasePriceMultipliedQuantiy:
              currencyFormatter.format(lineTotal, {
                code: CurrentPriceCurrencyCode,
              }),
          };
        }
        return currentLine;
      }),
    };

    newSubscription = updateSubscriptionWithUpdatedLine(newSubscription);
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );
    debouncedSave(newSubscription, line);
  };

  const handleReactivate = async () => {
    setShowReactivateModal(true);
  };

  const handleReactivateConfirm = async ({ nextBillingDate }) => {
    try {
      addLoadingToast();
      setIsLoadingReactivate(true);
      const res = await reactivateSc(
        customerId,
        subscriptionId,
        authToken,
        nextBillingDate,
      );

      handlePauseUpdate(res, t('subscription.reactivate_success'));
    } catch (err) {
      console.error(err);
      showErrorToast(t('subscription.reactivate_error'));
    } finally {
      removeLoadingToast();
      setIsLoadingReactivate(false);
    }
  };

  const launchBabUpdatedCompleteEvent = (
    lines: LineItem[],
    subscriptionContract: Subscription,
    discountCodes?: globalThis.Discount[],
  ) => {
    const customEventName = 'awtomic-bab-update-complete';
    const customEventDetail = {
      lines,
      subscriptionContract,
      discountCodes: discountCodes || [],
    };

    // send custom event so that merchants can listen when the bundle has
    // been eventually updated in our database and the Shopify hook has run
    const event = new CustomEvent(customEventName, {
      detail: customEventDetail,
    });
    window.dispatchEvent(event);
  };

  const updateLine = (
    lineId: string,
    productUpdated: GlobalVariant,
    quantity: number,
    pricingPolicy,
    subscriptionContract: Subscription,
  ) => {
    const isSameProduct = ({ SubscriptionContractLineId }) => {
      return SubscriptionContractLineId === lineId;
    };
    const newLine = (line: LineItem): LineItem =>
      isSameProduct(line)
        ? mergeGlobalProductInProduct(
            line,
            productUpdated,
            quantity,
            pricingPolicy,
          )
        : line;

    let newSubscription: Subscription = {
      ...(subscription as Subscription),
      NextBillingDate: subscriptionContract?.NextBillingDate,
      OriginalNextBillingDate: subscriptionContract.OriginalNextBillingDate,
      DeliveryPriceAmount: subscriptionContract.DeliveryPriceAmount,
      Lines: subscription.Lines?.map(newLine),
    };

    newSubscription = updateSubscriptionWithUpdatedLine(newSubscription);
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );
  };

  const handleUpdateProduct = async (
    productId: string,
    quantity: number,
    lineId: string,
    productUpdated: GlobalVariant,
  ) => {
    const newLine = [
      {
        id: lineId,
        input: {
          productVariantId: getGidFromIdForResource(
            productId,
            'ProductVariant',
          ),
          quantity,
        },
      },
    ];
    try {
      addLoadingToast();
      const { updatedPrices, subscriptionContract, lines } = await changeSCL(
        newLine,
        customerId,
        subscriptionId,
        authToken,
      );
      showSuccessToast(
        isSubscriptionInHeldOfInventory
          ? t('subscription.update_line_success_in_held_of_inventory')
          : t('subscription.update_line_success'),
      );

      const pricingPolicy = updatedPrices?.find(
        (updated) => lineId === updated.lineId,
      );

      const available = !!lines.find(({ id }) => id === lineId)?.available;

      updateLine(
        lineId,
        { ...productUpdated, available: productUpdated.available || available },
        quantity,
        pricingPolicy,
        subscriptionContract,
      );
    } catch (e) {
      console.error(e);
      showErrorToast(t('product_swap.update_error'));
    } finally {
      removeLoadingToast();
    }
  };

  const updateBundleLine = async (lineId, newBundleProps, pricingPolicy) => {
    const isSameProduct = ({ SubscriptionContractLineId }) => {
      return SubscriptionContractLineId === lineId;
    };
    const newLine = (line: LineItem): LineItem =>
      isSameProduct(line)
        ? mergeNewBundlePropsInLine(
            line,
            newBundleProps,
            pricingPolicy,
            subscription.CurrencyCode,
          )
        : line;

    let newSubscription: Subscription = {
      ...(subscription as Subscription),
      Lines: subscription.Lines?.map(newLine),
    };

    newSubscription = updateSubscriptionWithUpdatedLine(newSubscription);
    await queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );

    setIsBundleUpdating(false);
    removeLoadingToast();
    showSuccessToast(
      isSubscriptionInHeldOfInventory
        ? t('subscription.update_line_success_in_held_of_inventory')
        : t('subscription.update_line_success'),
    );
    setBundleIsReady(true);
  };

  const updateDynamicBundleLines = (
    lines: LineResponse[],
    subscriptionContract: Subscription,
    discounts: Discount[],
    lineId: string,
  ): Promise<void> => {
    const newSubscriptionLines = mergeUpdatedDynamicBaBLines(
      lines,
      subscription.Lines,
      discounts,
      lineId,
    );

    if (newSubscriptionLines.length === 0) {
      showErrorToast(t('bundles.update_error_reload'));
      return;
    }

    let newSubscription: Subscription = wrapDynamicBabItems({
      ...(subscription as Subscription),
      ...(subscriptionContract as Subscription),
      Lines: newSubscriptionLines,
    });

    newSubscription = updateSubscriptionWithUpdatedLine(newSubscription);

    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );

    setIsBundleUpdating(false);
    removeLoadingToast();
    showSuccessToast(
      isSubscriptionInHeldOfInventory
        ? t('subscription.update_dynamic_success_in_held_of_inventory')
        : t('subscription.update_dynamic_success'),
    );
    refetchUntilChange({ compareOnlyLines: true });
  };

  const addBundleLines = async () => {
    await refetchUntilChange({ compareOnlyLines: true });
    setIsBundleUpdating(false);
  };

  const babUpdatedWithBundleSuccess = async ({
    updatedPrices,
    subscriptionContract,
    lines,
    discountCodes,
    lineId,
    isDynamic,
    newBundleProps,
  }) => {
    const pricingPolicy = updatedPrices?.find(
      (updated) => lineId === updated.lineId,
    );

    let customEventDetail = {};
    let customEventName;
    if (isDynamic) {
      updateDynamicBundleLines(
        lines,
        subscriptionContract,
        discountCodes || [],
        lineId,
      );

      customEventName = 'awtomic-dynamic-box-updated';
      customEventDetail = {
        lines,
        subscriptionContract,
        discountCodes: discountCodes || [],
      };
    } else {
      const BaBOutOfStockVariants =
        lines.find(({ id }) => id === lineId)?.BaBOutOfStockVariants || [];
      await updateBundleLine(
        lineId,
        { ...newBundleProps, BaBOutOfStockVariants },
        pricingPolicy,
      );

      customEventName = 'awtomic-classic-box-updated';
      customEventDetail = {
        lineId,
        newBundleProps,
        pricingPolicy,
      };
    }

    // send custom event so that mechants can listen for box changes on the FE
    // to show custom UI afterwards
    const event = new CustomEvent(customEventName, {
      detail: customEventDetail,
    });
    window.dispatchEvent(event);
  };

  const swapProductForBundle = async (
    bundleProps,
    lineId: string,
    selectedItems?: any,
    sellingPlan?: SellingPlan,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
  ) => {
    addLoadingToast();
    setIsBundleUpdating(true);
    setBundleIsReady(false);
    try {
      const { productVariantId } = bundleProps;

      await updateBabProductForBundle({
        customerId,
        subscriptionId,
        authToken,
        productVariantId,
        lineId,
        selectedItems,
        bundleProps,
        sellingPlan,
        discountCodes,
        oneTimeAddon,
      });

      await addBundleLines();
    } catch (e) {
      console.error(e);
      showErrorToast(t('product_swap.update_error'));
    } finally {
      removeLoadingToast();
    }
  };

  const addBundleToSubscription = async (
    bundleProps,
    _lineId: string,
    selectedItems: any,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => {
    addLoadingToast();
    setIsBundleUpdating(true);
    setBundleIsReady(false);
    try {
      const { productVariantId } = bundleProps;

      await addBabProductForSubscription({
        customerId,
        subscriptionId,
        authToken,
        productVariantId,
        selectedItems,
        bundleProps,
        sellingPlan,
        discountCodes,
        oneTimeAddon,
        addonGroupId,
        forceSyncUpdate: true,
      });

      await addBundleLines();
    } catch (e) {
      console.error(e);
      showErrorToast(t('product_swap.update_error'));
    } finally {
      removeLoadingToast();
    }
  };

  const isSentryTestingEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.SENTRY_TEST,
  );

  const handleUpdateBundle = async (
    newBundleProps,
    lineId: string,
    itemsInBab?: any,
    sellingPlan?: string | number,
    discountCodes?: Discount[],
    oneTimeAddon?: boolean,
  ) => {
    addLoadingToast();
    setIsBundleUpdating(true);
    setBundleIsReady(false);
    let newLines;
    const isDynamic = newBundleProps.customAttributes.find(
      (prop) => prop.key === '_bundleType' && prop.value === 'dynamicBaB',
    );

    if (isDynamic) {
      newLines = generateNewDynamicSubscriptionLines(
        subscription,
        itemsInBab,
        lineId,
        oneTimeAddon,
      );
    } else {
      newLines = [
        {
          id: lineId,
          input: newBundleProps,
        },
      ];
    }

    try {
      const { updatedPrices, lines, subscriptionContract } = await changeSCL(
        newLines,
        customerId,
        subscriptionId,
        authToken,
        sellingPlan,
        discountCodes?.map((d) => d.DynamicDiscountCode || '').filter(Boolean),
        !!isDynamic,
      );

      babUpdatedWithBundleSuccess({
        updatedPrices,
        subscriptionContract,
        lines,
        discountCodes,
        lineId,
        isDynamic,
        newBundleProps,
      });
    } catch (e) {
      console.error(e);
      removeLoadingToast();
      showErrorToast(t('bundles.update_error'));
      setIsBundleUpdating(false);
    }
  };

  const handleAddAddons = async (
    addonsAdded: (GlobalVariant & {
      quantity: number;
      productName: string;
      pricingPolicy?: ComputedPricingPolicy;
      addonGroupId?: string;
    } & {
      line: { id: string; oneTimeAddon?: boolean };
    })[],
  ) => {
    const newAddonsLines = addonsAdded.map((addonLine) =>
      createNewAddonLine(
        addonLine,
        addonsSellingPolicy === 'ONETIME' || addonLine.line.oneTimeAddon,
      ),
    );
    let newSubscription: Subscription = {
      ...(subscription as Subscription),
      Lines: [...(subscription?.Lines || []), ...newAddonsLines],
    };
    showSuccessToast(t('addons.success_added'));

    newSubscription = updateSubscriptionWithUpdatedLine(newSubscription);
    queryClient.setQueryData<Subscription>(
      ['subscription', customerId, subscriptionId],
      (old) => ({
        ...old,
        ...newSubscription,
      }),
    );
  };

  const handleUpdateVariant = async (
    variantLine: GlobalVariant & { quantity: number },
    lineId: string,
  ) => {
    try {
      addLoadingToast();
      const variantInput = {
        productVariantId: variantLine.id,
        quantity: variantLine.quantity,
      };
      const newLine = [{ id: lineId, input: variantInput }];

      const { updatedPrices, subscriptionContract, lines } = await changeSCL(
        newLine,
        customerId,
        subscriptionId,
        authToken,
      );

      const IsSoldOut = !!lines.find(({ id }) => id === lineId)?.available;

      const pricingPolicy = updatedPrices?.find(
        (updated) => lineId === updated.lineId,
      );
      const oldLine = subscription?.Lines?.find(
        ({ SubscriptionContractLineId }) =>
          lineId === SubscriptionContractLineId,
      );

      const newVariantLine = updateVariantLine(
        oldLine,
        variantLine,
        IsSoldOut,
        pricingPolicy,
        subscription.CurrencyCode,
      );

      showSuccessToast(
        isSubscriptionInHeldOfInventory
          ? t('subscription.update_line_success_in_held_of_inventory')
          : t('subscription.update_line_success'),
      );

      const newSubscription: Subscription = updateSubscriptionWithUpdatedLine({
        ...(subscription as Subscription),
        NextBillingDate: subscriptionContract?.NextBillingDate,
        OriginalNextBillingDate: subscriptionContract?.OriginalNextBillingDate,
        DeliveryPriceAmount: subscriptionContract.DeliveryPriceAmount,
        Lines: (subscription?.Lines || []).map((line) => {
          if (line.SubscriptionContractLineId === lineId) {
            return newVariantLine;
          }
          return line;
        }),
      });
      queryClient.setQueryData<Subscription>(
        ['subscription', customerId, subscriptionId],
        (old) => ({
          ...old,
          ...newSubscription,
        }),
      );
    } catch (e) {
      showErrorToast(t('bundles.update_error'));
      console.error(e);
    } finally {
      removeLoadingToast();
    }
  };

  const getEditPaymentBtnText = () => {
    if (!hasPaymentMethod) {
      return t('titles.add');
    }
    if ((isActive && !paymentResponse) || isFailed) {
      return t('titles.edit');
    }

    return undefined;
  };

  const handleOnAddNewCard = () => {
    setSendEmailIfAddPaymentFails(true);
    onAddNewPaymentMethod();
  };

  const menuOptions: IMenuOption[] = [
    {
      text: t('subscription.update_current_payment_method'),
      type: 'update',
      action: () => {
        if (isStubbedData) {
          handleStubDataChanges();
          return;
        }
        handlePaymentUpdate();
      },
    },
    {
      text: t('subscription.add_new_card'),
      type: 'update',
      action: handleOnAddNewCard,
    },
  ];

  const isAutorenew = !subscription.BillingPolicyMaxCycles;
  const isPrepaidV2 = subscription?.Lines?.some(({ PrepaidV2 }) => PrepaidV2);
  const isPrepaidV3 = subscription?.Lines?.some(
    ({ PrepaidVersion }) => PrepaidVersion === 3,
  );
  const isPrepaidV1 = isPrepaid && !isPrepaidV2 && !isPrepaidV3;
  const anchors = get(subscription, 'DeliveryBatchingAnchor', []);
  const isBatched = anchors.length > 0;
  const isBillingOrUpdating =
    subscription?.IsBilling ||
    subscription?.IsUpdating ||
    subscription?.IsBillingScheduled;
  const isBatchedV2 =
    isObject(subscription?.BatchingConfiguration) &&
    subscription?.BatchingConfiguration?.anchor !== '';
  const hideSkipNextOrOrderNow =
    customerPortalSettings?.hideSkipNext ||
    customerPortalSettings?.hideOrderNow;

  const queryParamsNoSubscription = new URLSearchParams(window.location.search);
  queryParamsNoSubscription.delete('subscriptionId');

  const showPhoneManagement =
    shopNotifyBySMS ||
    (shopAddressCountry &&
      COUNTRIES_TO_SHOW_PHONE_MANAGEMENT.includes(shopAddressCountry));

  const isMerchantBannerActive =
    customerPortalSettings?.merchantBanner &&
    customerPortalSettings?.merchantBanner?.enabled;

  // Check if there's an address1 and country. If not, it's likely
  // local pickup or delivery. For now we're going to hide the shipment
  // info card for them

  const isLocalPickup = !isEmpty(subscription.DeliveryMethod?.pickupOption);
  const isLocalDelivery = !isEmpty(
    subscription.DeliveryMethod?.localDeliveryOption,
  );
  const address = isLocalPickup
    ? subscription.DeliveryMethod?.pickupOption?.location?.address
    : subscription.DeliveryMethod?.address;

  const localDeliveryInfo = isLocalDelivery
    ? subscription.DeliveryMethod.localDeliveryOption
    : null;

  if (forceUpdateError) {
    return <LoadingErrorPage />;
  }

  return (
    <GlobalModalProvider>
      <DetailWrapper className="bundle-details-wrapper">
        <FlexContainer className="bundle-flex-container">
          <Breadcrumbs
            items={[
              {
                displayName: t('titles.subscriptions'),
                href: `${APP_PROXY_URL}?${queryParamsNoSubscription.toString()}`,
              },
              {
                displayName: `${getIdFromGid(subscription.SubscriptionId)} ${
                  isPaused
                    ? `• ${t('subscription.paused')}`
                    : isCancelled
                      ? `• ${t('subscription.cancelled')}`
                      : isExpired
                        ? `• ${t('subscription.expired')}`
                        : ''
                }`,
                active: true,
              },
            ]}
            separator="/"
          />

          {!isShopifyLoggedIn ? (
            <CardButton onClick={logout}>{t('titles.logout')}</CardButton>
          ) : null}
        </FlexContainer>

        {isMerchantBannerActive && isMerchantBannerOpen && (
          <MerchantBanner
            onClose={() => setIsMerchantBannerOpen(false)}
            title={customerPortalSettings?.merchantBanner?.title}
            description={customerPortalSettings?.merchantBanner?.description}
            type={customerPortalSettings?.merchantBanner?.type}
            ctaButtonText={customerPortalSettings?.merchantBanner?.buttonText}
            ctaButtonUrl={customerPortalSettings?.merchantBanner?.buttonUrl}
            isDismissable={
              customerPortalSettings?.merchantBanner?.isDismissable
            }
            subscription={subscription}
          />
        )}

        {subscriptionIsInDunning &&
          hasPaymentMethod &&
          !isRetryingManually &&
          !isUpdatingPaymentMethod &&
          !isNewPaymentSaved &&
          subscription?.SubscriptionStatus === 'ACTIVE' && (
            <Alert status="error" style={{ margin: '10px 0' }}>
              <AlertIcon />

              <AlertDescription>
                {subscriptionBillingAttemptRetry
                  ? t('subscription.failed_payment_message', {
                      date: new Date(
                        subscriptionBillingAttemptRetry?.NextBillingDate,
                      ).toLocaleDateString(),
                    })
                  : t('subscription.failed_payment_message_no_date')}
                <LinkButton
                  className="subscription-payment-link-button"
                  onClick={handleOnAddNewCard}
                >
                  {t('subscription.failed_payment_cta')}
                </LinkButton>{' '}
                {!customerPortalSettings?.hideSkipNext && (
                  <>
                    {t('subscription.or').toLowerCase()}

                    <LinkButton
                      className="subscription-payment-link-button"
                      onClick={() => handleSkip()}
                    >
                      {t('subscription.skip_order_cta')}
                    </LinkButton>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

        {renderNotifs()}
        <Moments
          isStub={isStubbedData}
          refetchSubscription={refetchUntilChange}
          subscriptionId={subscriptionId}
          showErrorToast={showErrorToast}
        />
        {isSentryTestingEnabled && (
          <button
            type="button"
            onClick={() => {
              throw new Error('Sentry Test Error');
            }}
          >
            Break the world
          </button>
        )}
        {customerPortalSettings?.showLastOrder && <LastOrder />}
        <SubsDetailLayout className="bundle-subs-detail-layout">
          <div className="bundle-subs-detail-layout-column--left">
            <DetailTitle className="bundle-details-table-title">
              {subscription.ContainsGift
                ? t('subscription.your_next_gift_order')
                : t('subscription.your_next_order')}
              {!subscriptionBillingAttemptRetry &&
                subscription.NextBillingDate && (
                  <span>
                    {` • ${new Date(
                      subscription.NextBillingDate,
                    ).toLocaleDateString()}`}
                  </span>
                )}
            </DetailTitle>
            <ShowSm className="bundle-show-sm">
              <ActionButtonContainer
                className="bundle-action-button-container"
                columns={
                  isPaused ||
                  isCancelled ||
                  isFailed ||
                  subscriptionIsInDunning ||
                  isBatchedV2 ||
                  hideSkipNextOrOrderNow
                    ? 1
                    : 2
                }
              >
                <SkipNextButton
                  subscription={subscription as Subscription}
                  t={t}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  isStubbedData={isStubbedData}
                  handleStubDataChanges={handleStubDataChanges}
                  handleSkip={handleSkip}
                />
                <ReactivateButton
                  isPrepaid={isPrepaid}
                  isPrepaidV2={isPrepaidV2}
                  subscription={subscription as Subscription}
                  handleReactivate={handleReactivate}
                  isLoadingReactivate={isLoadingReactivate}
                  t={t}
                />
                <OrderNowButton
                  subscription={subscription as Subscription}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  shouldDisableOrderNowButton={shouldDisableOrderNowButton}
                  orderSubmitted={orderSubmitted}
                  setShowOrderNowConfirmationModal={
                    openOrderNowConfirmationModal
                  }
                  t={t}
                />
                <RetryPaymentButton
                  subscription={subscription as Subscription}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  onOrderNowConfirm={() => handleOrderNowClick(true)}
                  isLoadingOrderNow={isLoadingOrderNow}
                  orderSubmitted={orderSubmitted}
                  t={t}
                />
              </ActionButtonContainer>
            </ShowSm>
            <ProductsTable
              subscription={subscription}
              shuffleLineData={shuffleLineData}
              handleSkipClick={handleSkipClick}
              handleUnskipClick={handleUnskipClick}
              images={productImages}
              sellingPlans={sellingPlans}
              handleUpdateProduct={handleUpdateProduct}
              handleUpdateVariant={handleUpdateVariant}
              handleRemoveClick={handleRemoveClick}
              handleUpdateBundle={handleUpdateBundle}
              swapProductForBundle={swapProductForBundle}
              handleOnQuantityChange={handleOnQuantityChange}
              babProductVariants={babProductVariants || []}
              isLoadingShippingRate={isLoadingShippingRate}
              isSkippingShuffleProduct={isUpdatingShuffleProduct}
              shopConfiguration={shopConfiguration}
              disableCurrencySensitiveActions={
                isCurrencyDifferentThanShopDefaultCurrency
              }
            />

            {!isPrepaid &&
              !isPrepaidV2 &&
              !isCurrencyDifferentThanShopDefaultCurrency && (
                <AddonsProvider
                  onAddAddons={handleAddAddons}
                  onAddBundle={addBundleToSubscription}
                  addonsDiscounts={addonsDiscounts}
                  subscription={subscription}
                  sellingPlans={sellingPlans}
                  shopConfiguration={shopConfiguration}
                >
                  <Addons />
                </AddonsProvider>
              )}

            {!subscription.ContainsGift && (
              <BillingHistory
                billingAttempts={subscriptionBillingAttempts?.Items || []}
                originOrder={originOrder}
              />
            )}
          </div>
          <ActionColumLayout className="bundle-action-col-layout">
            <HideSm className="bundle-hide-sm">
              <ActionButtonContainer
                className="bundle-action-button-container"
                columns={
                  isPaused ||
                  isCancelled ||
                  isFailed ||
                  subscriptionIsInDunning ||
                  isBatchedV2 ||
                  hideSkipNextOrOrderNow
                    ? 1
                    : 2
                }
              >
                <SkipNextButton
                  subscription={subscription as Subscription}
                  t={t}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  isStubbedData={isStubbedData}
                  handleStubDataChanges={handleStubDataChanges}
                  handleSkip={handleSkip}
                />

                <ReactivateButton
                  isPrepaid={isPrepaid}
                  isPrepaidV2={isPrepaidV2}
                  subscription={subscription as Subscription}
                  handleReactivate={handleReactivate}
                  isLoadingReactivate={isLoadingReactivate}
                  t={t}
                />
                <OrderNowButton
                  subscription={subscription as Subscription}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  shouldDisableOrderNowButton={shouldDisableOrderNowButton}
                  orderSubmitted={orderSubmitted}
                  setShowOrderNowConfirmationModal={
                    openOrderNowConfirmationModal
                  }
                  t={t}
                />
                <RetryPaymentButton
                  subscription={subscription as Subscription}
                  subscriptionIsInDunning={subscriptionIsInDunning}
                  onOrderNowConfirm={() => handleOrderNowClick(true)}
                  isLoadingOrderNow={isLoadingOrderNow}
                  orderSubmitted={orderSubmitted}
                  t={t}
                />
              </ActionButtonContainer>
            </HideSm>
            {!isPrepaid ? (
              <SubsNextOrder
                subscription={subscription as Subscription}
                isBatched={isBatched || isBatchedV2}
                onUpdate={handlePauseUpdate}
                onError={isStubbedData ? handleStubDataChanges : showErrorToast}
                onSkipUpdate={handleSkipUpdate}
                isPrepaid={isPrepaid}
                isBillingOrUpdating={isBillingOrUpdating}
                shopConfiguration={shopConfiguration}
              />
            ) : (
              <SubsOrderFulfillment
                interval={deliveryInterval.toString()}
                intervalCount={+deliveryIntervalCount}
              />
            )}

            <SubsFrequency
              subscription={subscription}
              onUpdate={handleFrequencyUpdate}
              onError={isStubbedData ? handleStubDataChanges : showErrorToast}
              isPrepaid={isPrepaid}
              isPrepaidV2={isPrepaidV2}
              isBillingOrUpdating={isBillingOrUpdating}
              availableSellingPlans={availableSellingPlans}
            />

            {showPhoneManagement ? (
              <PhoneManagement
                shopNotifyBySMS={shopNotifyBySMS}
                subscription={subscription}
                onUpdate={handleCustomerUpdate}
                onError={isStubbedData ? handleStubDataChanges : showErrorToast}
              />
            ) : null}

            {address && (
              <Card
                ariaLabel={
                  isActive
                    ? isLocalPickup
                      ? t('aria_labels.pick_up_location')
                      : t('aria_labels.edit_shipment_info')
                    : undefined
                }
                headerText={t(
                  `subscription.${
                    isLocalPickup
                      ? 'local_pickup'
                      : isLocalDelivery
                        ? 'local_delivery_info'
                        : 'shipment_info'
                  }`,
                )}
                btnText={isActive && t('titles.edit')}
                onClick={() => {
                  setModalOpen(true);
                }}
                className="bundle-card--address"
                testId="address-card"
                disableButton={isBillingOrUpdating || isPrepaidV1}
              >
                {!isLocalPickup && (
                  <ShipmentName className="bundle-shipment-name">
                    {address?.name ||
                      `${address?.firstName} ${address?.lastName}`}
                  </ShipmentName>
                )}
                <ShipmentBody className="bundle-shipment-body">
                  {isLocalPickup && (
                    <>
                      <b>
                        {
                          subscription.DeliveryMethod?.pickupOption
                            ?.presentmentTitle
                        }
                      </b>
                      <br />
                    </>
                  )}
                  {address?.address1} <br />
                  {address?.address2 ? (
                    <>
                      {address?.address2}
                      <br />
                    </>
                  ) : null}
                  {address?.city},{' '}
                  {address?.provinceCode && `${address?.provinceCode}`}{' '}
                  {address?.zip}
                  <br />
                  {address?.country} <br />
                </ShipmentBody>
                {isLocalDelivery && localDeliveryInfo && (
                  <LocalDeliveryInfo className="bundle-local-delivery-info">
                    <p>{localDeliveryInfo.phone}</p>
                    <p>{localDeliveryInfo.instructions}</p>
                  </LocalDeliveryInfo>
                )}
              </Card>
            )}
            {!subscription.ContainsGift && (
              <MenuWrapper>
                <Card
                  ariaLabel={t('aria_labels.edit_payment_method')}
                  className="bundle-card--payment-method"
                  headerText={t('subscription.payment_method')}
                  testId="payment-card"
                  showButton={
                    (!isPrepaidV2 || isAutorenew) && !isBillingOrUpdating
                  }
                >
                  {(!isPrepaidV2 || isAutorenew) && !isBillingOrUpdating && (
                    <>
                      {(isActive && !paymentResponse && hasPaymentMethod) ||
                      isFailed ? (
                        <ActionsMenu
                          menuOptions={menuOptions}
                          ButtonComponent={PaymentMenuButton}
                          text={getEditPaymentBtnText()}
                          buttonClassnames="bundle-card--payment-method__button"
                          buttonAriaLabel={t('aria_labels.edit_payment_method')}
                        />
                      ) : null}
                    </>
                  )}

                  <SubsDetailPayment
                    subscription={subscription as Subscription}
                    response={paymentResponse}
                    isInDunning={subscriptionIsInDunning}
                    isFailed={isFailed && !isRetryingManually}
                    isPaymentMethodUpdated={
                      isUpdatingPaymentMethod || isNewPaymentSaved
                    }
                  />
                </Card>
              </MenuWrapper>
            )}
            <DiscountCodeCard
              appliedDiscountCodes={subscription.DiscountCodes}
            />
            <PauseButton
              subscription={subscription as Subscription}
              isStubbedData={isStubbedData}
              handleStubDataChanges={handleStubDataChanges}
              onPauseModalOpen={onPauseModalOpen}
              t={t}
            />
            <CancelButton
              subscription={subscription as Subscription}
              isStubbedData={isStubbedData}
              handleStubDataChanges={handleStubDataChanges}
              onCancelModalOpen={onCancelModalOpen}
              t={t}
            />
          </ActionColumLayout>
        </SubsDetailLayout>
        {isActive && (
          <AddressModal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            setDisableOpenButton={setDisableOpenButton}
            subscription={subscription as Subscription}
            onUpdate={
              isStubbedData ? handleStubDataChanges : handleAddressUpdate
            }
          />
        )}
        <PauseModal
          open={showPauseModal}
          setOpen={setShowPauseModal}
          subscription={subscription as Subscription}
          onUpdate={handlePauseUpdate}
          onError={isStubbedData ? handleStubDataChanges : showErrorToast}
        />
        <ReactivateModal
          open={showReactivateModal}
          setOpen={setShowReactivateModal}
          onConfirm={handleReactivateConfirm}
          shopConfiguration={shopConfiguration}
          batchingSettings={subscription?.BatchingConfiguration}
        />

        <OrderNowModal
          containsGift={subscription.ContainsGift}
          open={showOrderNowConfirmationModal}
          setOpen={openOrderNowConfirmationModal}
          setClose={closeOrderNowConfirmationModal}
          subscription={subscription as Subscription}
          onUpdate={
            isStubbedData
              ? () => {
                  handleStubDataChanges();
                  closeOrderNowConfirmationModal();
                }
              : () => handleOrderNowClick()
          }
          loading={isLoadingOrderNow}
        />

        {isPaymentMethodPanelOpen ? (
          <PaymentMethodPanel
            open={isPaymentMethodPanelOpen}
            onClose={onClosePaymentMethodPanel}
            onSave={handleNewPaymentSave}
            isLoading={isLoadingNewPaymentMethod}
          />
        ) : null}
        {isCancelModalOpen ? (
          <ModalManagerProvider
            isOpen={isCancelModalOpen}
            onClose={handleOnCancelModalClose}
          >
            <CancelMitigationModal
              isBatchedV2={isBatchedV2}
              isPrepaidMultiOrder={isPrepaidV2 || isPrepaidV3}
              hasDiscountCodes={
                //do not take in account discount codes from dynamic BaB
                subscription?.DiscountCodes?.filter(
                  ({ title }) => !title?.startsWith('box-discount'),
                ).length > 0
              }
              shouldExpireContract={shouldExpireContract}
              cancelMitigationDiscountAlreadyApplied={
                subscription?.CancelMitigationDiscount
              }
              hasManualDiscounts={subscription.Lines?.some(
                ({ DiscountAllocations }) =>
                  DiscountAllocations?.some(
                    ({ discount }) => discount?.type === 'MANUAL',
                  ),
              )}
            />
          </ModalManagerProvider>
        ) : null}
        {isPauseModalOpen ? (
          <ModalManagerProvider
            isOpen={isPauseModalOpen}
            onClose={handleOnPauseModalClose}
          >
            <PauseMitigationModal subscription={subscription as Subscription} />
          </ModalManagerProvider>
        ) : null}
        {isStubbedData && isPreview && (
          <PreviewToast>
            <PreviewLogo />
            <CloseButton onClick={handleClickClosePreview}>
              {t('preview.close')}
            </CloseButton>
          </PreviewToast>
        )}
      </DetailWrapper>
    </GlobalModalProvider>
  );
};

const PreviewToast = styled.div`
  position: fixed;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  bottom: 10%;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 1000;
  display: flex;
  box-shadow: 0px 4px 16px 0px #00000014;
  svg {
    margin-top: 2px;
  }
`;
const CloseButton = styled.span`
  border: 1px solid #dfe3e8;
  box-shadow: 0px 1px 0px rgba(0, 0, 0, 0.05);
  padding: 1px 10px;
  border-radius: 5px;
  font-weight: 500;
  color: #202223;
  cursor: pointer;
`;

const PaymentMenuButton = styled(CardButton)`
  position: absolute;
  top: -30px;
  right: 0px;
  z-index: 1;
`;
